import { useQuery } from '@tanstack/react-query';

import { useState } from 'react';

import { Box, Button, CircularProgress, ListItem, Stack } from '@mui/material';

import { useApiClient } from '@@common';

import { ServiceConnectionClient } from '../../../../api/api';
import IconComponent from '../../../../common/components/IconComponent';
import useServiceConnectionMetadata from '../../../ServiceConnections/Hooks/useServiceConnectionMetadata';
import ServiceConnectionDialog from '../../../ServiceConnections/ServiceConnectionDialog';
import { ServiceConnectionRefModel } from '../../Models/ServiceConnectionModel';
import { IconServiceConnection } from '../Icons';
import ListWithFilter from '../ListWithFilter';

type Props = {
	serviceConnectionType?: string;
	serviceConnectionCategory?: string;
	value?: ServiceConnectionRefModel;
	confirmLabel?: string;
	onSave: (value: ServiceConnectionRefModel) => boolean | Promise<boolean>;
	onCancel: () => void;
};

export default function ServiceConnectionPicker({ serviceConnectionType, serviceConnectionCategory, value, confirmLabel = 'Select', onSave, onCancel }: Props) {
	const allowedTypes = serviceConnectionType?.split('|').map(type => type.trim()).filter(Boolean);
	const client = useApiClient(ServiceConnectionClient);
	const { serviceConnectionByName } = useServiceConnectionMetadata();
	const shouldFilterCategoryByMetadata = !!serviceConnectionCategory && !serviceConnectionType;
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [selectedServiceConnectionId, setSelectedServiceConnectionId] = useState(value?.Id);
	const [isSaving, setIsSaving] = useState(false);

	const {
		data: serviceConnections,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			'service-connection',
			serviceConnectionType,
			serviceConnectionCategory,
			shouldFilterCategoryByMetadata ? serviceConnectionByName.size : undefined,
		],
		queryFn: async () => {
			const results = await Promise.all((allowedTypes?.length ? allowedTypes : [undefined]).map(type => client.search({
				type,
				category: shouldFilterCategoryByMetadata ? undefined : serviceConnectionCategory,
			})));
			const items = results.flatMap(result => result.items);

			if (!shouldFilterCategoryByMetadata) {
				return items;
			}

			return items.filter((item) => {
				const metadata = serviceConnectionByName.get(item.type);
				return item.categories?.includes(serviceConnectionCategory!) || metadata?.categories?.includes(serviceConnectionCategory!);
			});
		},
	});

	const handleSave = async () => {
		const selectedConnection = serviceConnections?.find((connection) => connection.id === selectedServiceConnectionId);
		if (!selectedConnection) {
			return;
		}

		setIsSaving(true);
		try {
			if (await onSave({ Id: selectedConnection.id, Name: selectedConnection.name })) {
				onCancel();
			}
		} finally {
			setIsSaving(false);
		}
	};

	const handleCreateSuccess = (connection: ServiceConnectionRefModel) => {
		setSelectedServiceConnectionId(connection.Id);
		void refetch();
	};

	return (
		<>
			<Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
				<ListWithFilter
					items={serviceConnections ?? []}
					icon={(item) => {
						const metadata = serviceConnectionByName.get(item.type);
						return metadata?.iconUrl || metadata?.iconName ? (
							<IconComponent iconUrl={metadata.iconUrl} iconName={metadata.iconName} />
						) : (
							<IconServiceConnection />
						);
					}}
					isLoading={isLoading}
					loadingIndicator={
						<ListItem sx={{ justifyContent: 'center', py: 3 }}>
							<CircularProgress size={24} aria-label="Loading service connections" />
						</ListItem>
					}
					selectedItemId={selectedServiceConnectionId}
					onSelected={(connection) => setSelectedServiceConnectionId(connection.id)}
					getDisplayName={(connection) => connection.name}
					getKey={(connection) => connection.id}
					placeholder="Search Service Connections"
					maxHeight="320px"
					sx={{ width: '100%', minWidth: 0, flex: 1 }}
				/>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
					<Button onClick={() => setCreateDialogOpen(true)}>Create New</Button>
					<Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
					<Button
						onClick={() => {
							void handleSave();
						}}
						variant="contained"
						loading={isSaving}
						disabled={!serviceConnections?.some((connection) => connection.id === selectedServiceConnectionId)}
					>
						{confirmLabel}
					</Button>
				</Box>
			</Stack>
			{createDialogOpen && (
				<ServiceConnectionDialog
					open={createDialogOpen}
					setOpen={setCreateDialogOpen}
					mode="create"
					serviceConnectionType={allowedTypes?.length === 1 ? allowedTypes[0] : undefined}
					serviceConnectionCategory={serviceConnectionCategory}
					onSuccess={handleCreateSuccess}
				/>
			)}
		</>
	);
}
